import { CheckCircle2, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { AttentionItem, AttentionSeverity } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminSection } from '@/components/shell/AdminSection';
import { Button } from '@/components/ui/button';
import { LoadError } from '@/components/ui/load-state';
import { useAttention } from '@/hooks/useAttention';
import { SEVERITY_LABELS, actionLabel, unavailableNotice } from '@/lib/attention';
import type { StatusTone } from '@/lib/runTelemetry';
import { cn } from '@/lib/utils';

const SEVERITY_TONE: Record<AttentionSeverity, StatusTone> = {
  critical: 'danger',
  warning: 'warning',
  info: 'neutral',
};

const STRIPE_CLASS: Record<AttentionSeverity, string> = {
  critical: 'bg-destructive',
  warning: 'bg-warning',
  info: 'bg-muted-foreground/40',
};

function AttentionRow({ item }: { item: AttentionItem }) {
  return (
    <li className="relative flex flex-col gap-3 py-3.5 pl-5 pr-5 sm:flex-row sm:items-center">
      <span aria-hidden="true" className={cn('absolute inset-y-2 left-0 w-1 rounded-r-full', STRIPE_CLASS[item.severity])} />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill tone={SEVERITY_TONE[item.severity]}>{SEVERITY_LABELS[item.severity]}</StatusPill>
          <p className="min-w-0 text-sm font-medium text-foreground">{item.title}</p>
        </div>
        <p className="text-xs leading-5 text-muted-foreground">{item.detail}</p>
      </div>
      <Button asChild variant="outline" size="sm" className="shrink-0 self-start sm:self-center">
        <Link to={item.path}>{actionLabel(item.area)}</Link>
      </Button>
    </li>
  );
}

/** The top of Home: what needs a platform admin, worst first, each with the page that deals with it. */
export function NeedsAttention() {
  const { attention, loading, error, refresh } = useAttention();
  const notice = unavailableNotice(attention?.unavailable);

  let body;
  if (!attention && loading) {
    body = (
      <div className="flex items-center gap-2 px-5 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Checking what needs you
      </div>
    );
  } else if (!attention && error) {
    body = <LoadError message="Could not check what needs you." onRetry={refresh} compact className="px-5 py-4" />;
  } else if (!attention || attention.items.length === 0) {
    body = (
      <p className="flex items-center gap-2 px-5 py-4 text-sm text-muted-foreground">
        <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
        Nothing needs you right now.
      </p>
    );
  } else {
    body = (
      <ul className="divide-y divide-border">
        {attention.items.map((item) => (
          <AttentionRow key={item.id} item={item} />
        ))}
      </ul>
    );
  }

  return (
    <AdminSection title="Needs attention" bodyClassName="p-0" className="mb-6">
      {body}
      {notice ? <p className="border-t border-border px-5 py-2.5 text-xs text-muted-foreground">{notice}</p> : null}
    </AdminSection>
  );
}
